import { API_URL } from '../utils/api';
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const TenantContext = createContext(null);

// API_URL imported from utils

export function TenantProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [entities, setEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState({
    timezones: [],
    currencies: [],
    countries: [],
    subscriberCategories: []
  });

  const fetchEntities = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.get(`${API_URL}/api/entities`, {
        withCredentials: true,
        headers
      });
      setEntities(data);
      
      // Auto-select entity based on user role
      if (data.length > 0) {
        if (user?.entity_id) {
          const userEntity = data.find(e => e.id === user.entity_id);
          setSelectedEntity(prev => userEntity || prev || data[0]);
        } else {
          setSelectedEntity(prev => prev || data[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching entities:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?.entity_id]);

  const fetchConfig = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [timezones, currencies, countries, categories] = await Promise.all([
        axios.get(`${API_URL}/api/config/timezones`, { withCredentials: true, headers }),
        axios.get(`${API_URL}/api/config/currencies`, { withCredentials: true, headers }),
        axios.get(`${API_URL}/api/config/countries`, { withCredentials: true, headers }),
        axios.get(`${API_URL}/api/config/subscriber-categories`, { withCredentials: true, headers })
      ]);
      
      setConfig({
        timezones: timezones.data,
        currencies: currencies.data,
        countries: countries.data,
        subscriberCategories: categories.data
      });
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchEntities();
    fetchConfig();
  }, [fetchEntities, fetchConfig]);

  const selectEntity = (entity) => {
    setSelectedEntity(entity);
  };

  const getCurrentTimezone = () => {
    if (selectedEntity?.timezone) {
      return selectedEntity.timezone;
    }
    return 'UTC';
  };

  const getCurrentCurrency = () => {
    if (selectedEntity?.currency_code) {
      const code = selectedEntity.currency_code;
      const match = config.currencies.find(c => c.code === code);
      if (match) return match;
      return { code, symbol: code, name: code };
    }
    return { code: 'USD', symbol: '$', name: 'US Dollar' };
  };

  const formatCurrency = (amount) => {
    const currency = getCurrentCurrency();
    return `${currency.symbol}${amount?.toLocaleString() || '0'}`;
  };

  const value = {
    entities,
    selectedEntity,
    selectEntity,
    loading,
    config,
    fetchEntities,
    getCurrentTimezone,
    getCurrentCurrency,
    formatCurrency
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}

export default TenantContext;
